import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { chevronDownOutline, chevronForwardOutline, chevronUpOutline, powerOutline } from 'ionicons/icons';
import { UserDto } from '../../core/models/api.models';
import { AuthService } from '../../core/services/auth.service';
import { SpotifyService } from '../../core/services/spotify.service';
import { UserService } from '../../core/services/user.service';
import { YoutubeService } from '../../core/services/youtube.service';
import { DisconnectResponse } from '../../core/models/disconnect.model';
import { DeleteAccountResponse } from '../../core/models/delete-account.model';

@Component({
  selector: 'app-account',
  templateUrl: './account.page.html',
  styleUrls: ['./account.page.scss']
})
export class AccountPage implements OnInit {
  user?: UserDto | null;
  loadingPremium = false;
  disconnecting: { spotify: boolean; youtube: boolean } = { spotify: false, youtube: false };
  revokeLinks: { spotify?: string; youtube?: string } = {};
  deleting = false;
  dangerOpen = false;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private spotifyService: SpotifyService,
    private youtubeService: YoutubeService,
    private router: Router,
    private toastCtrl: ToastController
  ) {
    addIcons({ powerOutline, chevronDownOutline, chevronUpOutline, chevronForwardOutline });
  }

  ngOnInit(): void {
    this.userService.loadMe().subscribe(user => (this.user = user));
  }

  togglePremium(): void {
    if (!this.user) {
      return;
    }
    this.loadingPremium = true;
    this.userService.updatePremium({ isPremium: !this.user.isPremium }).subscribe({
      next: user => {
        this.user = user;
        this.loadingPremium = false;
      },
      error: () => (this.loadingPremium = false)
    });
  }

  connectSpotify(): void {
    this.spotifyService.getConnectUrl().subscribe(url => {
      if (url) {
        window.location.href = url;
      }
    });
  }

  connectYoutube(): void {
    this.youtubeService.getConnectUrl().subscribe(url => {
      if (url) {
        window.location.href = url;
      }
    });
  }

  async confirmDelete(): Promise<void> {
    const message = 'Deleting your account will remove playlists and history. This cannot be undone.';
    const confirmed = await this.presentConfirm('Delete account', message, 'Delete account');
    if (!confirmed) {
      return;
    }
    this.deleteAccount();
  }

  private deleteAccount(): void {
    if (this.deleting) {
      return;
    }
    this.deleting = true;
    this.userService.deleteAccount().subscribe({
      next: (res: DeleteAccountResponse) => {
        this.presentToast(res.message || 'Account deleted', 'success');
        this.authService.logout();
        this.userService.setUser(null);
        this.router.navigateByUrl('/auth/login');
        this.deleting = false;
      },
      error: (err: any) => {
        const status = err?.status;
        if (status === 401 || status === 403) {
          this.presentToast('Session expired, please log in again', 'warning');
          this.logout();
        } else {
          this.presentToast(err?.error?.message || 'Account deletion failed', 'danger');
        }
        this.deleting = false;
      }
    });
  }

  disconnectSpotify(): void {
    if (this.disconnecting.spotify) {
      return;
    }
    this.disconnecting.spotify = true;
    this.handleDisconnect(this.spotifyService.disconnect(), 'Spotify');
  }

  disconnectYoutube(): void {
    if (this.disconnecting.youtube) {
      return;
    }
    this.disconnecting.youtube = true;
    this.handleDisconnect(this.youtubeService.disconnect(), 'YouTube');
  }

  private handleDisconnect(obs: any, provider: 'Spotify' | 'YouTube'): void {
    obs.subscribe({
      next: (res: DisconnectResponse) => {
        if (res.revokeUrl) {
          this.setRevokeLink(provider, res.revokeUrl);
        }
        this.userService.loadMe().subscribe(updated => {
          this.user = updated;
        });
        this.presentToast(`${provider} disconnected`);
        this.setDisconnecting(provider, false);
      },
      error: (err: any) => {
        const status = err?.status;
        if (status === 401 || status === 403) {
          this.presentToast(`Please reconnect ${provider} to continue`, 'warning');
        } else {
          this.presentToast(err?.error?.message || `${provider} disconnect failed`, 'danger');
        }
        this.setDisconnecting(provider, false);
      }
    });
  }

  private setRevokeLink(provider: 'Spotify' | 'YouTube', url: string): void {
    if (provider === 'Spotify') {
      this.revokeLinks.spotify = url;
    } else {
      this.revokeLinks.youtube = url;
    }
  }

  private setDisconnecting(provider: 'Spotify' | 'YouTube', value: boolean): void {
    this.disconnecting = {
      ...this.disconnecting,
      [provider.toLowerCase()]: value
    } as typeof this.disconnecting;
  }

  private async presentToast(message: string, color: 'success' | 'warning' | 'danger' = 'success'): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 2000,
      position: 'bottom',
      color
    });
    await toast.present();
  }

  private async presentConfirm(header: string, message: string, confirmText: string): Promise<boolean> {
    const alert = document.createElement('ion-alert');
    alert.header = header;
    alert.message = message;
    alert.buttons = [
      {
        text: 'Cancel',
        role: 'cancel'
      },
      {
        text: confirmText,
        role: 'destructive'
      }
    ];

    document.body.appendChild(alert);
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'destructive';
  }

  logout(): void {
    this.authService.logout();
    this.userService.setUser(null);
    this.router.navigateByUrl('/auth/login');
  }

  toggleDangerZone(): void {
    this.dangerOpen = !this.dangerOpen;
  }
}
